import type { TableSchema } from "@/models/duckDB";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { Table } from "apache-arrow";
import * as duckDB from "@duckdb/duckdb-wasm";

let db: duckDB.AsyncDuckDB;

/**
 * Extracts the dynamicColumns from the data into individual columns
 * and deletes the base dynamicColumns property
 * @param data - Incoming data to be cleaned
 */
function extractDynamicColumns(data: Record<string, any>) {
  if (!Object.keys(data).includes("dynamicColumns")) return;

  for (const dynamicColumn of data["dynamicColumns"]) {
    data[dynamicColumn["name"]] = dynamicColumn["values"];
  }

  delete data["dynamicColumns"];
}

/**
 * Cleans up the data before inserting it into the DB
 * @param data - Incoming data to be cleaned
 * @returns String with the data cleaned up to be used as a file for a table creation
 */
function cleanData(data: Record<string, any>): string {
  delete data["__typename"];

  extractDynamicColumns(data);

  const cleanupAttributes = (
    data: Record<string, any>,
    attributeKey: string
  ) => {
    if (!Object.keys(data).includes(attributeKey)) return;

    data[attributeKey].forEach(
      (attributeList: Record<string, any>[], index: number) => {
        // Delete if any, the __typename key
        attributeList.forEach((attribute: Record<string, any>) => {
          delete attribute["__typename"];
        });

        // Parse the contents as string
        data[attributeKey][index] = JSON.stringify(data[attributeKey][index]);
      }
    );
  };
  cleanupAttributes(data, "attributes");
  cleanupAttributes(data, "resourceAttributes");

  // Removes columns with '.' as DuckDB doesn't like those
  Object.keys(data).forEach((key) => {
    if (key.includes(".")) {
      const newKey = key.replace(/\.+/g, "_");
      data[newKey] = data[key];
      delete data[key];
    }
  });
  return JSON.stringify(data);
}

/**
 * Parses the results from a query into a row-based list of JSON objects
 * @param queryResults - Arrow Table object with the results of the query
 * @returns List of JSON objects with the parsed query results as row-based data
 */
function parseQueryResults(queryResults: Table<any>): Record<string, any>[] {
  const columnData: Record<string, any[]> = {};
  for (const field of queryResults.schema.fields) {
    const columnName = field.name;
    columnData[columnName] = queryResults
      .getChild(columnName)
      ?.toJSON() as any[];
  }
  const firstKey = Object.keys(columnData)[0];

  // Parse attributes from String to actual objects
  const parseAttributes = (
    colData: Record<string, any>,
    attributeKey: string
  ) => {
    if (attributeKey in colData) {
      (colData[attributeKey] as any[]).forEach(
        (value: string, index: number) => {
          colData[attributeKey][index] = JSON.parse(value);
        }
      );
    }
  };
  parseAttributes(columnData, "attributes");
  parseAttributes(columnData, "resourceAttributes");

  // Transforms column-based to row-based results
  return columnData[firstKey].map((_, index) => {
    const row: Record<string, any> = {};
    Object.keys(columnData).forEach((key) => {
      row[key] = columnData[key][index];
    });
    return row;
  });
}

/**
 * Updates the main table schema with the new fields contained in the tempTable schema or,
 * alters the type of the columns if there is a type mismatch.
 * @param tableName - Name of the main table to update the schema
 * @param tableSchema - Schema object of the 'tableName' table
 * @param tempTableName - Name of the temp table where the new data resides
 * @param tempTableSchema - Schema object of the table with possible new fields
 * @param sharedConnection - Optional shared connection to the DB.
 */
async function updateTableSchema(
  tableName: string,
  tableSchema: TableSchema,
  tempTableName: string,
  tempTableSchema: TableSchema,
  sharedConnection?: AsyncDuckDBConnection
): Promise<void> {
  const c = await getDBConnection(sharedConnection);

  let mainTableSchema = tableSchema;
  for (const tempColumSchema of tempTableSchema) {
    const tempColumnInMainSchema = mainTableSchema.filter(
      (col) => col.column_name === tempColumSchema.column_name
    );
    if (tempColumnInMainSchema.length === 0) {
      // New column values are set to NULL by default, we can change this if we wanted to
      await c.query(
        `ALTER TABLE ${tableName} ADD ${tempColumSchema.column_name} ${tempColumSchema.column_type}`
      );
    } else if (
      tempColumnInMainSchema[0].column_type !== tempColumSchema.column_type
    ) {
      // Review which table has data, and set the main table column type to match the one with data
      const mainTableNonNullCount = Number(
        (
          (await queryTable(
            tableName,
            `SELECT COUNT(${tempColumSchema.column_name}) as count FROM ${tableName} WHERE ${tempColumSchema.column_name} IS NOT NULL`,
            true,
            c
          )) as Record<string, any>[]
        )[0].count
      );
      const tempTableNonNullCount = Number(
        (
          (await queryTable(
            tempTableName,
            `SELECT COUNT(${tempColumSchema.column_name}) as count FROM ${tempTableName} WHERE ${tempColumSchema.column_name} IS NOT NULL`,
            true,
            c
          )) as Record<string, any>[]
        )[0].count
      );

      if (mainTableNonNullCount < tempTableNonNullCount)
        await c.query(
          `ALTER TABLE ${tableName} ALTER ${tempColumSchema.column_name} TYPE ${tempColumSchema.column_type}`
        );
    }
    // Refresh the main table schema
    mainTableSchema = await getTableSchema(tableName, c);
  }
  await closeDBConnection(c, sharedConnection);
}

/**
 * Initializes and assigns the reference to the DB
 */
export async function initDuckDB() {
  const JSDELIVR_BUNDLES = duckDB.getJsDelivrBundles();

  // Select a bundle based on browser checks
  const bundle = await duckDB.selectBundle(JSDELIVR_BUNDLES);

  const worker_url = URL.createObjectURL(
    new Blob([`importScripts("${bundle.mainWorker!}");`], {
      type: "text/javascript",
    })
  );

  // Instantiate the asynchronous version of DuckDB-wasm
  const worker = new Worker(worker_url);

  // This logger doesn't log anything to the console
  const voidLogger = new duckDB.VoidLogger();

  // This logger will log everything into the console
  const devLogger = new duckDB.ConsoleLogger(duckDB.LogLevel.WARNING);

  // We could also have the same ConsoleLogger but set the LogLevel Error only
  const logger =
    process.env.VUE_APP_BUILD_MODE === "devel" ? devLogger : voidLogger;
  db = new duckDB.AsyncDuckDB(logger, worker);

  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
}

/**
 * Starter function that initializes the DB (if required) and creates and returns
 * a new DB connection (if required as well)
 * @param sharedConnection Optional shared connection
 * @returns A new or shared DB connection
 */
async function getDBConnection(
  sharedConnection?: AsyncDuckDBConnection
): Promise<AsyncDuckDBConnection> {
  if (!db) await initDuckDB();
  return sharedConnection ?? (await db.connect());
}

/**
 * Closes the DB connection if it is not a shared connection
 * @param c Main connection used to connect to the DB
 * @param sharedConnection Optional shared connection object with other functions
 */
async function closeDBConnection(
  c: AsyncDuckDBConnection,
  sharedConnection?: AsyncDuckDBConnection
): Promise<void> {
  if (!sharedConnection) await c.close();
}

/**
 * Creates a new table into the DB with the data as the base values and schema.
 * @param tableName - Name of the table to be created
 * @param data - Data to be inserted into the table. Should not be an empty object
 * @param sharedConnection - Optional connection to the DB
 */
export async function createTable(
  tableName: string,
  data: Record<string, any>,
  sharedConnection?: AsyncDuckDBConnection
): Promise<void> {
  const c = await getDBConnection(sharedConnection);

  await db.registerFileText("file.json", cleanData(data));
  await c.insertJSONFromPath("file.json", { name: tableName });
  await closeDBConnection(c, sharedConnection);
}

/**
 * Executes a pragma command to verify if a table exists or not
 * @param tableName - Name of the table to check if exists
 * @param sharedConnection - Optional connection to the DB
 * @returns Boolean indicating if the table exists or not
 */
export async function tableExists(
  tableName: string,
  sharedConnection?: AsyncDuckDBConnection
): Promise<boolean> {
  const c = await getDBConnection(sharedConnection);

  let tableExists = true;
  try {
    await c.query(`PRAGMA TABLE_INFO('${tableName}');`);
  } catch (e: any) {
    tableExists = false;
  }
  await closeDBConnection(c, sharedConnection);
  return tableExists;
}

/**
 * Returns the schema definition for a given table.
 * The table should exist in the DB
 * @param tableName - Name of the table to get the schema
 * @param sharedConnection - Optional connection to the DB
 * @returns Parsed TableSchema object
 */
export async function getTableSchema(
  tableName: string,
  sharedConnection?: AsyncDuckDBConnection
): Promise<TableSchema> {
  const c = await getDBConnection(sharedConnection);

  const tableDescribeResults = await c.query(`DESCRIBE ${tableName};`);
  const columnNames = tableDescribeResults
    .getChild("column_name")
    ?.toJSON() as string[];
  const columnTypes = tableDescribeResults
    .getChild("column_type")
    ?.toJSON() as string[];
  const schema: TableSchema = [];
  columnNames.forEach((_, index) =>
    schema.push({
      column_name: columnNames[index],
      column_type: columnTypes[index],
    })
  );

  await closeDBConnection(c, sharedConnection);
  return schema;
}

/**
 * Inserts the data into the specified table. The table must have been created before.
 * It also updates the main table schema with the newer fields coming from the new data
 * @param tableName - Name of the main table to insert data
 * @param data - New data to be inserted
 * @param sharedConnection - Optional connection to the DB
 */
export async function insertIntoTable(
  tableName: string,
  data: Record<string, any>,
  sharedConnection?: AsyncDuckDBConnection
): Promise<void> {
  const c = await getDBConnection(sharedConnection);

  // Create a new table with the new incoming data
  const tempTableName = "temp";
  await dropTable(tempTableName, c);
  await createTable(tempTableName, data, c);

  const mainTableSchema = await getTableSchema(tableName);
  const tempTableSchema = await getTableSchema(tempTableName);

  // Add new columns to the main table if table doesn't have all the new data fields
  await updateTableSchema(
    tableName,
    mainTableSchema,
    tempTableName,
    tempTableSchema,
    c
  );

  const allTempColumnsNames = tempTableSchema
    .map((col) => col.column_name)
    .join(",");

  // We need to always specify the columns to be inserted when using another table as the values source
  await c.query(
    `INSERT INTO ${tableName} (${allTempColumnsNames}) SELECT ${allTempColumnsNames} FROM ${tempTableName};`
  );

  // We drop the auxiliary table now that we have added the fields
  await dropTable(tempTableName, c);
  await closeDBConnection(c, sharedConnection);
}

/**
 * Drops the table with the specified name, if exists
 * @param tableName - Name of table to drop
 * @param sharedConnection - Optional connection to the DB
 */
export async function dropTable(
  tableName: string,
  sharedConnection?: AsyncDuckDBConnection
): Promise<void> {
  const c = await getDBConnection(sharedConnection);
  await c.query(`DROP TABLE IF EXISTS ${tableName};`);
  await closeDBConnection(c, sharedConnection);
}

/**
 * Executes the query if the table specified exists. The table specified should match with the table queried
 * @param tableName - Name of table to query
 * @param query - SQL query-like string
 * @param shouldParseResults - Flag indication if results should be parsed to JSON object or stay as Apache Table
 * @param sharedConnection - Optional connection to the DB
 * @returns Results of the query either row-based when parsed on Apache.Table like else
 */
export async function queryTable(
  tableName: string,
  query: string,
  shouldParseResults: boolean,
  sharedConnection?: AsyncDuckDBConnection
): Promise<Record<string, any>[] | Table<any>> {
  const c = await getDBConnection(sharedConnection);

  if (!(await tableExists(tableName, sharedConnection))) {
    console.warn("The requested table cannot be queried as it doesn't exist");
    return shouldParseResults ? [] : new Table();
  }

  // TODO: Remove this hotfix once the issue is fixed in Squel
  // ISSUE: Whenever a string has '?' charts in it, Squel will attempt to replace them with parameters
  // if nothing is found, those are replaced with 'undefined' which messes with the data results
  const hotFixQuery = query.replace(/'undefined'/g, "?");
  const results: Table<any> = await c.query(hotFixQuery);
  await closeDBConnection(c, sharedConnection);
  return shouldParseResults ? parseQueryResults(results) : results;
}
